import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Image, Bot, Settings, User, LayoutGrid, Apple } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AnimatePresence, motion } from "framer-motion";

interface MenuDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const sheetCSS = `
.tecly-sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 850;
}
.tecly-sheet {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: 110px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(50px) saturate(180%);
  -webkit-backdrop-filter: blur(50px) saturate(180%);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 20px 60px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.5);
  padding: 14px 16px 18px;
  z-index: 900;
  transform-origin: bottom right;
}
.dark .tecly-sheet {
  background: rgba(20, 20, 30, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.12);
}
.tecly-sheet-handle {
  width: 36px;
  height: 5px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.18);
  margin: 0 auto 14px;
}
.dark .tecly-sheet-handle { background: rgba(255,255,255,0.25); }
`;

export const MenuDrawer = ({ open, onOpenChange }: MenuDrawerProps) => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onOpenChange(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const handleClose = () => onOpenChange(false);

  // Close menu first, then navigate after 200ms so the close animation can play
  const handleNavigate = (path: string) => {
    onOpenChange(false);
    setTimeout(() => navigate(path), 200);
  };

  return (
    <>
      <style>{sheetCSS}</style>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="tecly-sheet-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={handleClose}
            />
            <motion.div
              className="tecly-sheet"
              initial={{ opacity: 0, scale: 0.3, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.3, y: 40 }}
              transition={{ type: "spring", stiffness: 380, damping: 28 }}
              role="dialog"
              aria-modal="true"
            >
              <div className="tecly-sheet-handle" />
              <div className="grid grid-cols-3 gap-3">
                <SheetItem icon={<LayoutGrid className="w-7 h-7 text-blue-500" />} label="Bacheca" onClick={() => handleNavigate("/bacheca")} />
                <SheetItem icon={<Apple className="w-7 h-7 text-foreground" />} label="Apple Store" onClick={() => handleNavigate("/apple-store")} />
                <SheetItem icon={<Image className="w-7 h-7 text-foreground" />} label="Sfondi" onClick={() => handleNavigate("/sfondi")} />
                <SheetItem icon={<Bot className="w-7 h-7 text-primary" />} label="Tecly AI" onClick={() => handleNavigate("/tecly-ai")} />
                {userId && (
                  <SheetItem icon={<User className="w-7 h-7 text-primary" />} label="Profilo" onClick={() => handleNavigate(`/utente?id=${userId}`)} />
                )}
                <SheetItem icon={<Settings className="w-7 h-7 text-foreground" />} label="Impostazioni" onClick={() => handleNavigate("/profile")} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

const SheetItem = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-white/30 bg-white/60 dark:bg-white/10 dark:border-white/15 backdrop-blur-md transition-transform duration-200 active:scale-90"
  >
    {icon}
    <span className="text-xs font-medium text-foreground">{label}</span>
  </button>
);
